// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Driver Resources
// Upload safety docs, procedures, maps, FEMA guides for drivers
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef } from 'react';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
  IconUpload, IconFile, IconDownload, IconX, IconFileText,
} from '@tabler/icons-react';
import { GabButton, GabBadge, GabModal, GabInput, GabSelect, GabTextarea } from '../../components/gab';
import { notify } from '../../components/gab/GabNotifications';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';

const CATEGORY_OPTIONS = [
  { value: 'safety',      label: 'Safety' },
  { value: 'procedures',  label: 'Procedures' },
  { value: 'maps',        label: 'Maps' },
  { value: 'contracts',   label: 'Contracts' },
  { value: 'fema',        label: 'FEMA' },
  { value: 'other',       label: 'Other' },
];

const CATEGORY_COLORS: Record<string, string> = {
  safety: 'var(--color-error)', procedures: 'var(--color-primary-700)',
  maps: 'var(--color-success)', contracts: '#7c3aed',
  fema: 'var(--color-warning)', other: 'var(--color-neutral-500)',
};

const EMPTY_FORM = { title: '', category: '', description: '', uploadedBy: '' };

export default function Documents() {
  const { canEdit } = useModulePermission('debris');
  const { activeIncident, user } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    documents, loadingDocs,
    loadDocuments, createDocument, updateDocument, deleteDocument,
    uploadDocumentFile, getDocumentDownloadUrl,
  } = useDebrisStore();

  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState<string | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<any | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [viewTarget, setViewTarget] = useState<any | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState<number | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const filtered = useMemo(() => {
    let list = [...documents];
    if (catFilter && catFilter !== 'all') list = list.filter(d => d.category === catFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(d =>
        (d.title || '').toLowerCase().includes(q) ||
        (d.description || '').toLowerCase().includes(q),
      );
    }
    return list.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  }, [documents, catFilter, search]);

  const openCreate = () => {
    setEditTarget(null);
    setPendingFile(null);
    setForm({ ...EMPTY_FORM, uploadedBy: user?.email ?? '' });
    setModalOpen(true);
  };

  const openEdit = (d: any) => {
    setEditTarget(d);
    setPendingFile(null);
    setForm({
      title: d.title || '',
      category: d.category || '',
      description: d.description || '',
      uploadedBy: d.uploadedBy || '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      notify({ title: 'Required', message: 'Title is required', variant: 'warning' });
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title,
      notes: form.description,
      documentType: form.category,
      uploadedBy: form.uploadedBy,
      incidentId,
    };
    try {
      let recordId: number | null = editTarget?.id ?? null;
      if (editTarget) {
        await updateDocument(editTarget.id, payload);
      } else {
        // createDocument catches internally and returns null on failure — throw manually if so
        recordId = await createDocument(payload);
        if (!recordId) throw new Error('Record was not created — check console for details');
      }

      if (pendingFile && recordId) {
        try {
          await uploadDocumentFile(recordId, pendingFile);
          notify({ message: editTarget ? 'Document updated with file' : 'Document created with file', variant: 'success' });
        } catch (uploadErr: unknown) {
          // Record saved but file upload failed — show specific error
          notify({
            title: 'Record saved, file upload failed',
            message: uploadErr instanceof Error ? uploadErr.message : 'File upload error — try uploading again from the edit modal',
            variant: 'warning',
          });
        }
      } else {
        notify({ message: editTarget ? 'Document updated' : 'Document created', variant: 'success' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notify({ message: err instanceof Error ? err.message : 'Save failed', variant: 'error' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteDocument(deleteTarget.id);
      notify({ message: `${deleteTarget.title} deleted`, variant: 'default' });
      setDeleteTarget(null);
      if (viewTarget?.id === deleteTarget.id) setViewTarget(null);
    } catch (err: unknown) {
      notify({ message: err instanceof Error ? err.message : 'Delete failed', variant: 'error' });
    } finally { setSaving(false); }
  };

  const handleDownload = async (doc: any) => {
    if (!doc.fileId) return;
    setDownloading(doc.id);
    try {
      const url = await getDocumentDownloadUrl(doc.fileId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      notify({ message: err instanceof Error ? err.message : 'Download failed', variant: 'error' });
    } finally { setDownloading(null); }
  };

  const catLabel = (cat: string) => CATEGORY_OPTIONS.find(c => c.value === cat)?.label ?? (cat || '-');

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <p className="text-sm text-muted">Safety procedures, field guides, maps, FEMA documents, and contracts for crew and driver reference.</p>
        </div>
        <div className="flex gap-2">
          <GabButton variant="ghost" size="sm" leftIcon={<IconRefresh size={14} />}
            loading={loadingDocs} onClick={() => loadDocuments()}>Refresh</GabButton>
          {canEdit && (
            <GabButton variant="primary" size="sm" leftIcon={<IconPlus size={14} />} onClick={openCreate}>
              Upload Resource
            </GabButton>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4">
        <div style={{ flex: 1, maxWidth: 320, position: 'relative' }}>
          <IconSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-neutral-400)', pointerEvents: 'none' }} />
          <input
            type="text"
            placeholder="Search title, description..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="form-input"
            style={{ paddingLeft: 32, fontSize: 13 }}
          />
        </div>
        <GabSelect
          options={[{ value: 'all', label: 'All Categories' }, ...CATEGORY_OPTIONS]}
          value={catFilter ?? 'all'}
          onChange={v => setCatFilter(v === 'all' ? null : v)}
          style={{ width: 180 }}
        />
      </div>

      <p className="text-xs text-muted mb-3">{filtered.length} resource{filtered.length !== 1 ? 's' : ''}</p>

      {/* Resource cards grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 rounded" style={{ border: '2px dashed var(--color-neutral-200)' }}>
          {loadingDocs ? (
            <p className="text-muted">Loading...</p>
          ) : (
            <>
              <IconFileText size={40} style={{ color: 'var(--color-neutral-300)', margin: '0 auto 12px' }} />
              <p className="text-muted fw-semibold">No driver resources yet</p>
              <p className="text-xs text-muted mt-1 mb-4">Upload safety guides, procedures, maps, and FEMA documents for crew reference</p>
              {canEdit && (
                <GabButton variant="primary" size="sm" leftIcon={<IconUpload size={14} />} onClick={openCreate}>
                  Upload First Resource
                </GabButton>
              )}
            </>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {filtered.map(doc => (
            <div
              key={doc.id}
              onClick={() => setViewTarget(doc)}
              style={{
                border: '1px solid var(--color-neutral-200)',
                borderRadius: 8,
                padding: 16,
                cursor: 'pointer',
                background: '#fff',
                transition: 'box-shadow 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)')}
              onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
            >
              <div className="flex items-start gap-3">
                <div style={{
                  width: 40, height: 40, borderRadius: 8, flexShrink: 0,
                  background: `${CATEGORY_COLORS[doc.category] || 'var(--color-neutral-400)'}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <IconFile size={20} style={{ color: CATEGORY_COLORS[doc.category] || 'var(--color-neutral-400)' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p className="fw-semibold text-sm" style={{ marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.title || 'Untitled'}
                  </p>
                  <GabBadge variant="neutral" style={{ fontSize: 10, padding: '1px 6px', background: `${CATEGORY_COLORS[doc.category] || '#666'}15`, color: CATEGORY_COLORS[doc.category] || '#666', border: 'none' }}>
                    {catLabel(doc.category)}
                  </GabBadge>
                  {doc.description && (
                    <p className="text-xs text-muted mt-2" style={{ overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {doc.description}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex justify-between items-center mt-3 pt-3" style={{ borderTop: '1px solid var(--color-neutral-100)' }}>
                <div className="flex items-center gap-1 text-xs text-muted">
                  {doc.fileId ? (
                    <><IconFile size={12} /><span>File attached</span></>
                  ) : (
                    <span style={{ color: 'var(--color-warning)' }}>No file</span>
                  )}
                </div>
                <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                  {doc.fileId && (
                    <button
                      className="btn btn-ghost btn-xs"
                      title="Download"
                      onClick={() => handleDownload(doc)}
                      disabled={downloading === doc.id}
                    >
                      <IconDownload size={13} />
                    </button>
                  )}
                  {canEdit && (
                    <>
                      <button className="btn btn-ghost btn-xs" title="Edit" onClick={() => openEdit(doc)}>
                        <IconPencil size={13} />
                      </button>
                      <button className="btn btn-ghost btn-xs" title="Delete" style={{ color: 'var(--color-error)' }} onClick={() => setDeleteTarget(doc)}>
                        <IconTrash size={13} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      <GabModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Resource' : 'Upload Driver Resource'}
        size="md"
      >
        <div className="stack" style={{ gap: 16 }}>
          <GabInput
            label="Title"
            required
            value={form.title}
            onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
            placeholder="e.g. Debris Removal Safety Guide"
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <GabSelect
              label="Category"
              options={CATEGORY_OPTIONS}
              value={form.category || null}
              onChange={v => setForm(prev => ({ ...prev, category: v ?? '' }))}
              placeholder="Select category..."
            />
            <GabInput
              label="Uploaded By"
              value={form.uploadedBy}
              onChange={e => setForm(prev => ({ ...prev, uploadedBy: e.target.value }))}
            />
          </div>
          <GabTextarea
            label="Description"
            value={form.description}
            onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Brief description of this resource..."
            rows={3}
          />

          {/* File attachment */}
          <div>
            <label className="form-label">Attach File</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.xlsx,.pptx,.zip,.png,.jpg,.jpeg,.txt"
              style={{ display: 'none' }}
              onChange={e => {
                const f = e.target.files?.[0] ?? null;
                setPendingFile(f);
              }}
            />
            {pendingFile ? (
              <div className="flex items-center gap-2 p-3 rounded" style={{ border: '1px solid var(--color-primary-700)', background: 'var(--color-primary-50)' }}>
                <IconFile size={18} style={{ color: 'var(--color-primary-700)', flexShrink: 0 }} />
                <span className="text-sm fw-semibold" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {pendingFile.name}
                </span>
                <span className="text-xs text-muted">({(pendingFile.size / 1024).toFixed(0)} KB)</span>
                <button className="btn btn-ghost btn-xs" onClick={() => { setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                  <IconX size={14} />
                </button>
              </div>
            ) : editTarget?.fileId ? (
              <div className="flex items-center gap-2 p-3 rounded" style={{ border: '1px solid var(--color-neutral-200)', background: 'var(--color-neutral-50)' }}>
                <IconFile size={18} style={{ color: 'var(--color-neutral-500)', flexShrink: 0 }} />
                <span className="text-sm text-muted" style={{ flex: 1 }}>File already attached — select a new file to replace it</span>
                <GabButton variant="outline" size="xs" leftIcon={<IconUpload size={12} />} onClick={() => fileInputRef.current?.click()}>
                  Replace
                </GabButton>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{
                  width: '100%', padding: '20px 16px', borderRadius: 8, cursor: 'pointer',
                  border: '2px dashed var(--color-neutral-300)', background: 'var(--color-neutral-50)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  transition: 'border-color 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--color-primary-700)')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--color-neutral-300)')}
              >
                <IconUpload size={24} style={{ color: 'var(--color-neutral-400)' }} />
                <span className="text-sm fw-semibold">Click to attach a file</span>
                <span className="text-xs text-muted">PDF, Word, Excel, PowerPoint, ZIP, or image</span>
              </button>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-2">
            <GabButton variant="ghost" size="sm" onClick={() => setModalOpen(false)}>Cancel</GabButton>
            <GabButton variant="primary" size="sm" loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Upload Resource'}
            </GabButton>
          </div>
        </div>
      </GabModal>

      {/* Detail View Modal */}
      <GabModal
        opened={!!viewTarget}
        onClose={() => setViewTarget(null)}
        title="Resource Detail"
        size="md"
      >
        {viewTarget && (
          <div className="stack" style={{ gap: 16 }}>
            <div className="flex items-start gap-3">
              <div style={{
                width: 48, height: 48, borderRadius: 10, flexShrink: 0,
                background: `${CATEGORY_COLORS[viewTarget.category] || 'var(--color-neutral-400)'}15`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <IconFile size={24} style={{ color: CATEGORY_COLORS[viewTarget.category] || 'var(--color-neutral-400)' }} />
              </div>
              <div>
                <p className="fw-bold" style={{ fontSize: 16 }}>{viewTarget.title}</p>
                <GabBadge variant="neutral" style={{ marginTop: 4, background: `${CATEGORY_COLORS[viewTarget.category] || '#666'}15`, color: CATEGORY_COLORS[viewTarget.category] || '#666', border: 'none' }}>
                  {catLabel(viewTarget.category)}
                </GabBadge>
              </div>
            </div>

            {viewTarget.description && (
              <p className="text-sm" style={{ padding: 12, borderRadius: 8, background: 'var(--color-neutral-50)', border: '1px solid var(--color-neutral-100)' }}>
                {viewTarget.description}
              </p>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <p className="text-xs text-muted fw-semibold mb-1">Uploaded By</p>
                <p className="text-sm">{viewTarget.uploadedBy || '-'}</p>
              </div>
            </div>

            {/* File download */}
            <div style={{ padding: 16, borderRadius: 8, border: '1px solid var(--color-neutral-200)', background: 'var(--color-neutral-50)' }}>
              <p className="text-sm fw-semibold mb-3">Attached File</p>
              {viewTarget.fileId ? (
                <div className="flex items-center gap-3">
                  <IconFile size={20} style={{ color: 'var(--color-primary-700)' }} />
                  <span className="text-sm" style={{ flex: 1 }}>Document file</span>
                  <GabButton
                    variant="primary" size="sm"
                    leftIcon={<IconDownload size={14} />}
                    loading={downloading === viewTarget.id}
                    onClick={() => handleDownload(viewTarget)}
                  >
                    Download
                  </GabButton>
                </div>
              ) : (
                <p className="text-sm text-muted">No file attached to this resource.</p>
              )}
            </div>

            {canEdit && (
              <div className="flex justify-end gap-2 pt-2" style={{ borderTop: '1px solid var(--color-neutral-100)' }}>
                <GabButton variant="ghost" size="sm" leftIcon={<IconPencil size={13} />}
                  onClick={() => { setViewTarget(null); openEdit(viewTarget); }}>
                  Edit
                </GabButton>
                <GabButton variant="ghost" size="sm" leftIcon={<IconTrash size={13} />}
                  style={{ color: 'var(--color-error)' }}
                  onClick={() => { setViewTarget(null); setDeleteTarget(viewTarget); }}>
                  Delete
                </GabButton>
              </div>
            )}
          </div>
        )}
      </GabModal>

      {/* Delete confirmation */}
      <GabModal
        opened={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Resource"
        size="sm"
      >
        <p className="text-sm mb-4">Delete <strong>{deleteTarget?.title}</strong>? This cannot be undone.</p>
        <div className="flex justify-end gap-3">
          <GabButton variant="ghost" size="sm" onClick={() => setDeleteTarget(null)}>Cancel</GabButton>
          <GabButton variant="destructive" size="sm" loading={saving} onClick={handleDelete}>Delete</GabButton>
        </div>
      </GabModal>
    </div>
  );
}
